import React from "react";
import { format } from "date-fns";
import clsx from "clsx";
import {
  HiOutlineLocationMarker,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineLightningBolt,
  HiOutlineChartPie,
  HiOutlinePhone,
  HiOutlineLogin,
  HiOutlineLogout,
  HiOutlineEye,
} from "react-icons/hi";
import { Card } from "../UI";
import { VISIT_TYPE_MAP, VISIT_STATUSES } from "../../pages/Visits/visitConstants";

// Minutes → "2h 15m"
export const fmtMins = (mins) => {
  if (mins === null || mins === undefined) return "—";
  const m = Math.max(0, Math.round(mins));
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}`;
};

const utilTone = (u) =>
  u === null || u === undefined ? "bg-secondary-300" : u >= 70 ? "bg-success-500" : u >= 40 ? "bg-warning-500" : "bg-danger-500";

export const UtilisationBar = ({ value, className }) =>
  value === null || value === undefined ? (
    <span className="text-xs text-secondary-400 whitespace-nowrap">No attendance</span>
  ) : (
    <div className={clsx("flex items-center gap-2", className)}>
      <div className="w-14 h-2 bg-secondary-200 rounded-full overflow-hidden flex-shrink-0">
        <div className={clsx("h-full rounded-full", utilTone(value))} style={{ width: `${value}%` }} />
      </div>
      <span className="text-sm font-semibold text-secondary-700 whitespace-nowrap">{value}%</span>
    </div>
  );

const OutcomeChips = ({ outcomes }) => {
  const list = Object.values(outcomes || {});
  if (!list.length) return <span className="text-xs text-secondary-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((o) => (
        <span
          key={o.label}
          className="px-1.5 py-0.5 rounded text-[11px] font-semibold border whitespace-nowrap"
          style={{ color: o.color, borderColor: `${o.color}55`, backgroundColor: `${o.color}12` }}
        >
          {o.label} {o.count}
        </span>
      ))}
    </div>
  );
};

const StatTile = ({ icon: Icon, tone, value, label, sub }) => (
  <Card padding="p-2.5 sm:p-3 lg:p-4">
    <div className="flex items-center gap-2 sm:gap-3">
      <div className={clsx("w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full flex items-center justify-center flex-shrink-0", tone)}>
        <Icon className="w-4 h-4 lg:w-5 lg:h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-sm sm:text-base lg:text-lg font-bold text-gray-900">{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
        {sub && <div className="text-[11px] text-gray-400 truncate">{sub}</div>}
      </div>
    </div>
  </Card>
);

/**
 * Daily Work report → "Visits & Productivity": team totals plus a per-employee
 * table (visits, visit time, punctuality, outcomes, talk + visit = productive
 * time vs attendance).
 */
export const VisitProductivitySection = ({ summary, users, onViewUser }) => {
  const withActivity = users.filter(
    (u) => (u.visits?.scheduled || 0) > 0 || (u.productivity?.productiveMinutes || 0) > 0 || u.attendance?.status !== "absent"
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <HiOutlineLocationMarker className="w-5 h-5 text-primary-600" />
        <h2 className="text-base lg:text-lg font-semibold text-gray-900">Visits & Productivity</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 lg:gap-4">
        <StatTile
          icon={HiOutlineCheckCircle}
          tone="bg-green-100 text-green-600"
          value={`${summary.totalVisitsCompleted || 0} / ${summary.totalVisits || 0}`}
          label="Visits done / scheduled"
        />
        <StatTile
          icon={HiOutlineXCircle}
          tone="bg-red-100 text-red-600"
          value={summary.totalVisitsMissed || 0}
          label="Missed / cancelled"
        />
        <StatTile
          icon={HiOutlineClock}
          tone="bg-blue-100 text-blue-600"
          value={fmtMins(summary.totalVisitMinutes || 0)}
          label="Time on visits"
        />
        <StatTile
          icon={HiOutlineLightningBolt}
          tone="bg-purple-100 text-purple-600"
          value={fmtMins(summary.totalProductiveMinutes || 0)}
          label="Productive time"
          sub={`Talk ${fmtMins(summary.totalTalkMinutes || 0)} + visits`}
        />
        <StatTile
          icon={HiOutlineChartPie}
          tone="bg-orange-100 text-orange-600"
          value={summary.avgUtilisation === null || summary.avgUtilisation === undefined ? "—" : `${summary.avgUtilisation}%`}
          label="Avg utilisation"
          sub="Productive ÷ attendance"
        />
      </div>

      <Card padding="p-0">
        {withActivity.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500">No visits or activity for this date</p>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    {["Employee", "Visits", "Visit time", "On time", "Outcomes", "Talk time", "Productive", "Utilisation", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {withActivity.map((u) => {
                    const v = u.visits || {};
                    const p = u.productivity || {};
                    return (
                      <tr key={u.userId} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900 whitespace-nowrap">{u.name}</p>
                          <p className="text-xs text-gray-500">{u.department}</p>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-semibold text-gray-900">{v.completed || 0}</span>
                          <span className="text-gray-400"> / {v.scheduled || 0}</span>
                          {(v.noShow || 0) + (v.cancelled || 0) > 0 && (
                            <span className="block text-xs text-red-500">{v.noShow + v.cancelled} missed</span>
                          )}
                          {v.inProgress > 0 && <span className="block text-xs text-cyan-600">{v.inProgress} in progress</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className="font-medium text-gray-900">{fmtMins(v.actualMinutes)}</span>
                          {v.plannedMinutes > 0 && <span className="block text-xs text-gray-400">planned {fmtMins(v.plannedMinutes)}</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {v.onTime + v.late > 0 ? (
                            <>
                              <span className={clsx("font-medium", v.late ? "text-orange-600" : "text-green-600")}>{v.onTimeRate}%</span>
                              {v.late > 0 && <span className="block text-xs text-orange-500">{v.late} late</span>}
                            </>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 min-w-[160px]">
                          <OutcomeChips outcomes={v.outcomes} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{fmtMins(p.talkMinutes)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-semibold text-gray-900">{fmtMins(p.productiveMinutes)}</span>
                          {p.attendanceMinutes > 0 && (
                            <span className="block text-xs text-gray-400">of {fmtMins(p.attendanceMinutes)} worked</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <UtilisationBar value={p.utilisation} />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => onViewUser(u.userId)}
                            aria-label={`View ${u.name} day`}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                          >
                            <HiOutlineEye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile */}
            <div className="md:hidden divide-y divide-gray-100">
              {withActivity.map((u) => {
                const v = u.visits || {};
                const p = u.productivity || {};
                return (
                  <button key={u.userId} type="button" onClick={() => onViewUser(u.userId)} className="w-full text-left p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-gray-900 truncate">{u.name}</p>
                      <UtilisationBar value={p.utilisation} />
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
                      <span>
                        Visits <b>{v.completed || 0}</b>/{v.scheduled || 0}
                      </span>
                      <span>Visit time {fmtMins(v.actualMinutes)}</span>
                      <span>Talk {fmtMins(p.talkMinutes)}</span>
                      <span>
                        Productive <b>{fmtMins(p.productiveMinutes)}</b>
                      </span>
                    </div>
                    <div className="mt-2">
                      <OutcomeChips outcomes={v.outcomes} />
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

const fmtTime = (d) => (d ? format(new Date(d), "hh:mm a") : "");

/**
 * Employee day view (Daily Work → user details): visit stats, productivity
 * and a chronological timeline of check-in, call blocks, visits, check-out.
 */
export const UserVisitProductivity = ({ report }) => {
  const v = report?.visitStats || {};
  const p = report?.productivity || {};
  const timeline = report?.timeline || [];

  return (
    <div className="p-3 sm:p-4 border border-gray-200 rounded-xl space-y-4">
      <h4 className="font-medium text-gray-900 flex items-center gap-2">
        <HiOutlineLightningBolt className="w-4 h-4 text-primary-600" />
        Productivity & Visits
      </h4>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          ["Talk time", fmtMins(p.talkMinutes), "bg-blue-50 text-blue-600"],
          ["Visit time", fmtMins(p.visitMinutes), "bg-cyan-50 text-cyan-600"],
          ["Productive", fmtMins(p.productiveMinutes), "bg-purple-50 text-purple-600"],
          ["Utilisation", p.utilisation === null || p.utilisation === undefined ? "No attendance" : `${p.utilisation}%`, "bg-orange-50 text-orange-600"],
        ].map(([label, value, tone]) => (
          <div key={label} className={clsx("text-center p-2.5 rounded-xl", tone.split(" ")[0])}>
            <div className={clsx("text-base sm:text-lg font-bold", tone.split(" ")[1])}>{value}</div>
            <div className="text-xs text-gray-500">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
        <span>
          Visits <b className="text-gray-900">{v.completed || 0}</b> done of {v.scheduled || 0}
        </span>
        {(v.noShow || 0) + (v.cancelled || 0) > 0 && <span className="text-red-500">{v.noShow + v.cancelled} missed/cancelled</span>}
        {v.onTime + v.late > 0 && (
          <span>
            On time <b className="text-gray-900">{v.onTimeRate}%</b>
          </span>
        )}
        {v.repeatVisits > 0 && <span>{v.repeatVisits} repeat</span>}
        {p.idleMinutes !== null && p.idleMinutes !== undefined && <span>Idle {fmtMins(p.idleMinutes)}</span>}
      </div>
      {Object.keys(v.outcomes || {}).length > 0 && <OutcomeChips outcomes={v.outcomes} />}

      {/* Day timeline */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Day timeline</p>
        {timeline.length === 0 ? (
          <p className="text-sm text-gray-400">No activity recorded</p>
        ) : (
          <ol className="relative border-l-2 border-gray-200 ml-2 space-y-3">
            {timeline.map((t, i) => {
              let Icon = HiOutlinePhone;
              let dot = "bg-blue-500";
              let title = "";
              let detail = "";
              if (t.type === "check_in") {
                Icon = HiOutlineLogin;
                dot = "bg-green-500";
                title = `${fmtTime(t.at)} · Checked in`;
              } else if (t.type === "check_out") {
                Icon = HiOutlineLogout;
                dot = "bg-gray-500";
                title = `${fmtTime(t.at)} · Checked out`;
              } else if (t.type === "calls") {
                title = `${fmtTime(t.from)}${t.count > 1 ? ` – ${fmtTime(t.to)}` : ""} · ${t.count} call${t.count === 1 ? "" : "s"}`;
                detail = `${t.connected} connected · ${fmtMins(t.talkSeconds / 60)} talk`;
              } else {
                const type = VISIT_TYPE_MAP[t.visitType] || VISIT_TYPE_MAP.site_visit;
                const status = VISIT_STATUSES[t.status] || VISIT_STATUSES.scheduled;
                Icon = type.icon;
                dot = t.started ? "bg-cyan-500" : "bg-gray-300";
                title = `${fmtTime(t.from)}${t.to ? ` – ${fmtTime(t.to)}` : ""} · ${type.label} · ${t.lead?.name || "Customer"}`;
                detail = [
                  status.label,
                  t.started ? `planned ${fmtMins(t.plannedMinutes)}, actual ${fmtMins(t.actualMinutes)}` : `planned ${fmtMins(t.plannedMinutes)}`,
                  t.outcome,
                  t.autoClosed ? "⚠ auto-closed" : null,
                ]
                  .filter(Boolean)
                  .join(" · ");
              }
              return (
                <li key={i} className="ml-4">
                  <span className={clsx("absolute -left-[9px] w-4 h-4 rounded-full ring-4 ring-white flex items-center justify-center", dot)}>
                    <Icon className="w-2.5 h-2.5 text-white" />
                  </span>
                  <p className="text-sm font-medium text-gray-900">{title}</p>
                  {detail && <p className="text-xs text-gray-500">{detail}</p>}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
};
